/**
 * Loads a script.
 * @param {string} src - Url of script to load.
 * @returns {Promise}
 */
function isDialogOpen() {
  return document.querySelector('dialog[open]');
}

function loadScript(src) {
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.onload = resolve;
    s.onerror = reject;
    document.body.appendChild(s);
  });
}

/* global DetailsDisclosure, trapFocus, removeTrapFocus */
// ===== SCROLL PREVENTION UTILITY =====
const ScrollPrevention = {
  scrollPosition: 0,
  isActive: false,

  prevent() {
    if (this.isActive) return;

    this.isActive = true;
    this.scrollPosition = window.pageYOffset;
    document.body.classList.add('cart-drawer-open');

    if (window.innerWidth < 768) {
      document.documentElement.classList.add('cart-drawer-open');
    } else {
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.scrollPosition}px`;
      document.body.style.width = '100%';
    }
  },

  restore() {
    if (!this.isActive) return;

    this.isActive = false;
    document.body.classList.remove('cart-drawer-open');

    if (window.innerWidth < 768) {
      document.documentElement.classList.remove('cart-drawer-open');
    } else {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, this.scrollPosition);
    }
  }
};

// Single source of truth for scroll state management
function updateScrollState() {
  const cartDrawer = document.querySelector('cart-drawer');
  const cartDrawerDisclosure = document.querySelector('cart-drawer-disclosure');

  const isDrawerOpen = (cartDrawer && cartDrawer.hasAttribute('open')) ||
                      (cartDrawerDisclosure && cartDrawerDisclosure.hasAttribute('open'));

  if (isDrawerOpen) {
    ScrollPrevention.prevent();
    if (cartDrawer) {
      cartDrawer.style.height = '100vh';
      cartDrawer.style.top = '0px';

      if (window.innerWidth < 768) {
        requestAnimationFrame(() => {
          cartDrawer.style.touchAction = 'none';
          cartDrawer.style.overscrollBehavior = 'none';
          const drawerContent = cartDrawer.querySelector('.drawer__content');
          if (drawerContent) {
            drawerContent.style.touchAction = 'pan-y';
            drawerContent.style.overscrollBehavior = 'contain';
            drawerContent.style.overflowY = 'auto';
            drawerContent.style.webkitOverflowScrolling = 'touch';
          }
        });
      }
    }
  } else {
    ScrollPrevention.restore();
  }
}

/* global theme, loadScript, ScrollPrevention, updateScrollState */

if (!customElements.get('cart-drawer-disclosure')) {
  class CartDrawerDisclosure extends DetailsDisclosure {
    constructor() {
      super();
      this.openBtn = this.querySelector('summary');
      this.closeBtn = this.querySelector('.js-close');
      this.overlay = document.getElementById('cart-drawer').querySelector('.overlay');
    }

    /**
     * Handles 'click' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      if (evt.target.matches('.js-close')) return;
      this.close();
      updateScrollState();
    }

    /**
     * Opens the details element.
     */
    open() {
      this.overlay.classList.add('is-visible');
      updateScrollState();
      super.open();
      trapFocus(this);
      ScrollPrevention.prevent();

      // Create event handler variables (so the bound event listeners can be removed).
      this.clickHandler = this.clickHandler || this.handleClick.bind(this);
      this.keyupHandler = (evt) => evt.key === 'Escape' && this.close();

      // Add event listeners (for while disclosure is open).
      this.addEventListener('click', this.clickHandler);
      this.addEventListener('keyup', this.keyupHandler);
      this.overlay.addEventListener('click', this.clickHandler);
    }

    /**
     * Closes the details element.
     */
    close() {
      if (isDialogOpen()) return;
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer.classList.contains('is-cart-drawer-open')) {
        cartDrawer.classList.remove('is-cart-drawer-open');
      }
      this.overlay.classList.remove('is-visible');
      super.close();
      removeTrapFocus(this.openBtn);
      updateScrollState();

      this.removeEventListener('click', this.clickHandler);
      this.removeEventListener('keyup', this.keyupHandler);
      this.overlay.removeEventListener('click', this.clickHandler);
    }
  }

  customElements.define('cart-drawer-disclosure', CartDrawerDisclosure);
}

/* global SideDrawer */

if (!customElements.get('cart-drawer')) {
  class CartDrawer extends SideDrawer {
    constructor() {
      super();
      this.cartSummary = this.querySelector('.cart-drawer__summary');
      this.init();
      this.bindEvents();
    }

    disconnectedCallback() {
      document.removeEventListener('dispatch:cart-drawer:refresh', this.refreshHandler);
      document.removeEventListener('on:bfcache:load-restore', this.refreshHandler);
      document.removeEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
      document.removeEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);

      if (Shopify.designMode) {
        document.removeEventListener('shopify:section:select', this.sectionSelectHandler);
        document.removeEventListener('shopify:section:deselect', this.sectionDeselectHandler);
      }

      if (this.dcbLoadedHandler) {
        document.removeEventListener('shopify:payment_button:loaded', this.dcbLoadedHandler);
      }
    }

    init() {
      const cartIcon = document.getElementById('cart-icon');
      const cartDrawer = document.querySelector('cart-drawer');
      const cartContent = document.getElementById('cart-drawer');
      const headerSections = document.querySelectorAll('.shopify-section-group-header-group');
      const closeBtnDrawer = document.getElementById('drawer__close-btn');

      if (!cartIcon || !cartDrawer || !cartContent) return;

      const enableHoverOpen = cartIcon.dataset.hoverOpen === 'true';
      let isHoverOpen = false;

      const isDrawerOpen = () =>
        cartDrawer.hasAttribute('open') && cartDrawer.getAttribute('aria-modal') === 'true';

      const openDrawer = () => {
        this.open(cartIcon);
        isHoverOpen = true;
      };

      const closeDrawer = () => {
        this.close();
        updateScrollState();
        if (cartDrawer.classList.contains('is-cart-drawer-open')) {
          cartDrawer.classList.remove('is-cart-drawer-open');
        }
      };

      if (window.innerWidth < 768) {
        cartContent.style.touchAction = 'pan-y';
        const drawerContent = cartContent.querySelector('.drawer__content');
        if (drawerContent) {
          drawerContent.style.touchAction = 'pan-y';
          drawerContent.style.overscrollBehavior = 'contain';
          let startY = 0;

          drawerContent.addEventListener('touchstart', function(e) {
            startY = e.touches[0].clientY;
          }, { passive: true });

          drawerContent.addEventListener('touchmove', function(e) {
            const currentY = e.touches[0].clientY;
            const deltaY = startY - currentY;
            const scrollTop = drawerContent.scrollTop;
            const scrollHeight = drawerContent.scrollHeight;
            const clientHeight = drawerContent.clientHeight;
            if ((scrollTop <= 0 && deltaY < 0) ||
                (scrollTop + clientHeight >= scrollHeight && deltaY > 0)) {
              e.preventDefault();
            }
          }, { passive: false });
        }
      }

      // Click to open
      cartIcon.addEventListener('click', (evt) => {
        evt.preventDefault();
        openDrawer();
      });

      // Keyboard open (spacebar)
      cartIcon.addEventListener('keydown', (evt) => {
        if (evt.key === ' ') {
          evt.preventDefault();
          openDrawer();
        }
      });

      // Close on drawer close button
      if (closeBtnDrawer) {
        if (isDialogOpen()) return;
        closeBtnDrawer.addEventListener('click', (evt) => {
          evt.preventDefault();
          closeDrawer();
        });
      }

      // Hover-based open/close only on desktop
      if (window.innerWidth >= 1024 && enableHoverOpen) {
        // Hover to open
        cartIcon.addEventListener('mouseenter', (evt) => {
          evt.preventDefault();
          openDrawer();
        });

        // Leave cart drawer content → close
        cartContent.addEventListener('mouseleave', (evt) => {
          if (isDialogOpen()) return;
          if (isHoverOpen && isDrawerOpen()) {
            evt.preventDefault();
            closeDrawer();
          }
        });

        // Hover on header → close (even if cart content wasn't hovered)
        headerSections.forEach((header) => {
          if (isDialogOpen()) return;
          header.addEventListener('mouseenter', (evt) => {
            if (isHoverOpen && isDrawerOpen()) {
              evt.preventDefault();
              closeDrawer();
            }
          });
        });
      }
    }

    bindEvents() {
      if (Shopify.designMode) {
        this.sectionSelectHandler = this.handleSectionSelect.bind(this);
        this.sectionDeselectHandler = this.handleSectionDeselect.bind(this);
        document.addEventListener('shopify:section:select', this.sectionSelectHandler);
        document.addEventListener('shopify:section:deselect', this.sectionDeselectHandler);
      }

      this.openDrawerViaEventHandler = this.handleDrawerOpenViaEvent.bind(this);
      this.closeDrawerViaEventHandler = this.close.bind(this, null);
      document.addEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
      document.addEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);

      if (this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
        this.dcbLoadedHandler = this.dcbLoadedHandler || CartDrawer.recalculateCssVarHeights;
        document.addEventListener('shopify:payment_button:loaded', this.dcbLoadedHandler);
      }

      this.refreshHandler = this.refresh.bind(this);
      document.addEventListener('dispatch:cart-drawer:refresh', this.refreshHandler);
      document.addEventListener('on:bfcache:load-restore', this.refreshHandler);
    }

    /**
     * Handle when the section is selected in the Theme Editor
     * @param {object} evt - Event object.
     */
    handleSectionSelect(evt) {
      if (evt.target === this.closest('.shopify-section')) this.open();
    }

    /**
     * Handle when the section is de-selected in the Theme Editor
     * @param {object} evt - Event object.
     */
    handleSectionDeselect(evt) {
      if (evt.target === this.closest('.shopify-section')) this.close();
    }

    /**
     * Handle when the drawer is opened via an event
     * @param {object} evt - Event object.
     */
    handleDrawerOpenViaEvent(evt) {
      this.open(evt.detail ? evt.detail.opener : null);
    }

    /**
     * Opens the drawer.
     * @param {Element} [opener] - Element that triggered opening of the drawer.
     * @param {Element} [elementToFocus] - Element to focus after drawer opened.
     */
    open(opener, elementToFocus) {
      // Get the quick add drawer web component, if it's currently open and close it
      const quickAddDrawer = document.querySelector('quick-add-drawer[aria-hidden="false"]');
      const overlay = document.querySelector('.js-overlay.is-visible');

      if (quickAddDrawer) {
        if (overlay) overlay.style.transitionDelay = '200ms';
        quickAddDrawer.close();
      }

      setTimeout(() => {
        super.open(opener, elementToFocus, () => {
          updateScrollState();
          if (this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
            CartDrawer.recalculateCssVarHeights();
          }
        });

        if (this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
          CartDrawer.recalculateCssVarHeights();
        }

        if (overlay) overlay.style.transitionDelay = '';
      }, quickAddDrawer ? 200 : 0);
    }

    close(callback) {
      super.close(callback);
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && cartDrawer.classList.contains('is-cart-drawer-open')) {
        cartDrawer.classList.remove('is-cart-drawer-open');
      }
      updateScrollState();
    }

    /**
     * Attempts to refresh any cart items (if present). Failing that, it refreshes the entire cart
     * drawer
     * @param {boolean} [dontRefreshCartItems=false] - Prevents the refresh of cart items, and does
     * a straight refresh of the whole cart
     */
    async refresh(dontRefreshCartItems) {
      try {
        const cartItems = this.querySelector('cart-items');
        if (cartItems && !dontRefreshCartItems) {
          cartItems.refresh();
        } else {
          const response = this.getSectionsToRender().map((section) => section.section);
          const cartResponse = await fetch(`?sections=${response.join(',')}`);
          const sections = await cartResponse.json();
          this.renderContents({ sections }, false);
          CartDrawer.recalculateCssVarHeights();
        }
      } catch (error) {
        console.log(error); // eslint-disable-line
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg?.textContent
          }
        }));
      }
    }

    /**
     * Renders the contents of the specified sections to update.
     * @param {object} data - Cart data object.
     * @param {boolean} [openDrawer=true] - Open the cart drawer after rendering sections.
     */
    async renderContents(data, openDrawer = true) {
      if (!this.scriptsLoaded) {
        if (!document.querySelector(`script[src="${theme.scripts.cartItems}"]`)) {
          await loadScript(theme.scripts.cartItems);
        }

        if (!document.querySelector(`script[src="${theme.scripts.shippingCalculator}"]`) && this.dataset.shippingCalculator) {
          await loadScript(theme.scripts.countryProvinceSelector);
          await loadScript(theme.scripts.shippingCalculator);
        }

        this.scriptsLoaded = true;
      }

      this.getSectionsToRender().forEach((section) => {
        const el = document.getElementById(section.id);
        if (el) {
          el.innerHTML = CartDrawer.getElementHTML(
            data.sections[section.section],
            section.selector
          );
        }
      });

      if (openDrawer && this.getAttribute('open') === null) {
        setTimeout(() => this.open());
      }
    }

    /**
     * Returns an array of objects containing required section details.
     * @returns {Array}
     */
    getSectionsToRender() {
      return [
        {
          id: 'cart-drawer',
          section: this.closest('.shopify-section').id.replace('shopify-section-', ''),
          selector: 'cart-drawer'
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section'
        }
      ];
    }

    /**
     * Dispatches an event to recalculate the data-css-var-heights on the page. Useful for
     * updating the height of the sticky cart buttons
     */
    static recalculateCssVarHeights() {
      window.requestAnimationFrame(() => {
        document.dispatchEvent(new CustomEvent('on:css-var-height:update'));
      });
    }

    /**
     * Gets the innerHTML of an element.
     * @param {string} html - Section HTML.
     * @param {string} selector - CSS selector for the element.
     * @returns {string}
     */
    static getElementHTML(html, selector) {
      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;

      return tmpl.content.querySelector(selector).innerHTML;
    }
  }

  customElements.define('cart-drawer', CartDrawer);
}

// Custom drawer close button handler
document.addEventListener('click', function (evt) {
  const closeBtnDrawer = evt.target.closest('#drawer__close-btn');
  const cartDrawer = document.querySelector('cart-drawer');
  const overlay = document.querySelector('.js-overlay');

  if (closeBtnDrawer && cartDrawer) {
    evt.preventDefault();
    cartDrawer.classList.remove('is-cart-drawer-open');
    cartDrawer.removeAttribute('open');
    updateScrollState();

    if (overlay) {
      overlay.classList.remove('is-visible');
    }
  }
});